import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SafeAvatarImage } from '@/components/ui/safe-avatar-image';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Search, CheckCircle, XCircle, Clock, Shield, ArrowLeft, Loader2, Instagram, Youtube, Plus, User, Globe } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { LoadingFallback } from '@/components/LoadingFallback';
import { useDebounce } from '@/hooks/useDebounce';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { getProxiedImageUrl } from '@/utils/imageProxy';
import { useCreators } from '@/hooks/useCreators';

const formatFollowerCount = (count: number | null): string => {
  if (!count) return '0';
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toString();
};

export const ClaimCreatorProfile: React.FC = () => {
  const { currentUser } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('search');
  const [selectedPlatform, setSelectedPlatform] = useState<'tiktok' | 'instagram' | 'youtube'>('tiktok');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [primaryPlatform, setPrimaryPlatform] = useState<string>('');
  const [stagedAccounts, setStagedAccounts] = useState<Array<{ username: string; display_name: string; avatar_url: string; bio: string; platform: string; follower_count: number; video_count: number; like_count: number; is_verified: boolean }>>([]);

  // Live API search
  const { data: apiData, isLoading } = useCreators({
    platform: selectedPlatform,
    query: debouncedSearch,
    enabled: debouncedSearch.trim().length > 0,
  });

  const searchResults = apiData?.creators || [];

  // My claim requests
  const { data: myRequests, isLoading: requestsLoading } = useQuery({
    queryKey: ['my-creator-claims', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return [];
      const { data, error } = await supabase
        .from('creators')
        .select('id, username, display_name, avatar_url, platform, follower_count, claim_status, claimed_at, primary_platform')
        .eq('claimed_by_profile_id', currentUser.id)
        .in('claim_status', ['pending', 'approved', 'rejected'])
        .order('claimed_at', { ascending: false });
      if (error) throw error;
      if (data && data.length > 0) {
        const existing = (data as any[]).find((d: any) => d.primary_platform);
        if (existing && !primaryPlatform) {
          setPrimaryPlatform(existing.primary_platform);
        }
      }
      return data || [];
    },
    enabled: !!currentUser?.id,
  });

  // Batch submit all staged accounts
  const batchClaimMutation = useMutation({
    mutationFn: async (accounts: typeof stagedAccounts) => {
      const results = [];
      for (const creator of accounts) {
        const { data, error } = await supabase.functions.invoke('claim-creator-profile', {
          body: {
            platforms: { [creator.platform]: creator.username },
            displayName: creator.display_name,
            bio: creator.bio || '',
            avatarUrl: creator.avatar_url,
            followerCount: creator.follower_count,
            videoCount: creator.video_count,
            likeCount: creator.like_count,
            isVerified: creator.is_verified,
          },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        results.push(data);
      }
      return results;
    },
    onSuccess: () => {
      toast({ title: 'All accounts linked!', description: 'Your linked accounts are pending admin review.' });
      setStagedAccounts([]);
      queryClient.invalidateQueries({ queryKey: ['my-creator-claims'] });
      setActiveTab('requests');
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to link accounts', description: error.message, variant: 'destructive' });
    },
  });

  const addStagedAccount = (creator: typeof stagedAccounts[0]) => {
    if (stagedAccounts.some(a => a.platform === creator.platform && a.username === creator.username)) {
      toast({ title: 'Already added', description: `@${creator.username} on ${creator.platform} is already in your list.`, variant: 'destructive' });
      return;
    }
    setStagedAccounts(prev => [...prev, creator]);
    setSearchTerm('');
    toast({ title: 'Account added', description: `@${creator.username} added. Add more accounts or submit all together.` });
  };

  const removeStagedAccount = (index: number) => {
    setStagedAccounts(prev => prev.filter((_, i) => i !== index));
  };

  // Update primary platform
  const updatePrimaryPlatformMutation = useMutation({
    mutationFn: async (platform: string) => {
      if (!currentUser?.id) return;
      const { error } = await supabase
        .from('creators')
        .update({ primary_platform: platform })
        .eq('claimed_by_profile_id', currentUser.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Primary platform updated' });
      queryClient.invalidateQueries({ queryKey: ['my-creator-claims'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to update', description: error.message, variant: 'destructive' });
    },
  });

  const handlePrimaryPlatformChange = (value: string) => {
    setPrimaryPlatform(value);
    updatePrimaryPlatformMutation.mutate(value);
  };

  // Cancel a pending claim
  const cancelMutation = useMutation({
    mutationFn: async (creatorId: string) => {
      const { error } = await supabase
        .from('creators')
        .update({ claim_status: 'unclaimed', claimed_by_profile_id: null, claimed_at: null })
        .eq('id', creatorId)
        .eq('claimed_by_profile_id', currentUser?.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Claim cancelled' });
      queryClient.invalidateQueries({ queryKey: ['my-creator-claims'] });
    },
  });

  if (!currentUser) return <LoadingFallback />;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="text-yellow-600 border-yellow-600"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return null;
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'instagram': return <Instagram className="h-4 w-4" />;
      case 'youtube': return <Youtube className="h-4 w-4" />;
      case 'tiktok': return <span className="text-xs font-bold">TT</span>;
      default: return <User className="h-4 w-4" />;
    }
  };

  const getPlatformLabel = (p: string) => {
    if (p === 'tiktok') return 'TikTok';
    if (p === 'youtube') return 'YouTube';
    return 'Instagram';
  };

  return (
    <Layout>
      <PageHeader title="Claim Your Creator Profile" subtitle="Search for your social media profiles and claim them" />
      <div className="p-4 md:p-6 space-y-6 max-w-[1200px] mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate('/profile')} className="gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Profile
        </Button>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="search">Search & Link</TabsTrigger>
            <TabsTrigger value="requests">
              My Requests {myRequests && myRequests.length > 0 && `(${myRequests.length})`}
            </TabsTrigger>
          </TabsList>

          {/* Search & Link Tab */}
          <TabsContent value="search" className="space-y-4">
            {/* Platform selector */}
            <div className="flex items-center gap-2">
              {(['tiktok', 'instagram', 'youtube'] as const).map((p) => (
                <Button
                  key={p}
                  variant={selectedPlatform === p ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setSelectedPlatform(p); setSearchTerm(''); }}
                  className="gap-1.5"
                >
                  {getPlatformIcon(p)}
                  <span>{getPlatformLabel(p)}</span>
                </Button>
              ))}
            </div>

            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={`Search your ${getPlatformLabel(selectedPlatform)} username...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Loading */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {/* Empty states */}
            {!isLoading && !debouncedSearch.trim() && (
              <div className="text-center py-12 text-muted-foreground">
                <Globe className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Enter your username to find your profile on {getPlatformLabel(selectedPlatform)}</p>
              </div>
            )}

            {!isLoading && debouncedSearch.trim().length > 0 && searchResults.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <p>No results found for "{debouncedSearch}" on {getPlatformLabel(selectedPlatform)}</p>
              </div>
            )}

            {/* Results */}
            {searchResults.length > 0 && (
              <div className="space-y-3">
                {searchResults.map((creator) => {
                  const isStaged = stagedAccounts.some(a => a.platform === creator.platform && a.username === creator.username);
                  return (
                    <Card key={`${creator.platform}-${creator.username}`} className={`flex items-center p-4 gap-3 ${isStaged ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : ''}`}>
                      <Avatar className="h-12 w-12">
                        <SafeAvatarImage src={getProxiedImageUrl(creator.avatar_url)} className="object-cover" />
                        <AvatarFallback>{(creator.display_name || creator.username)?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate flex items-center gap-1.5">
                          {creator.display_name || creator.username}
                          {creator.is_verified && <CheckCircle className="h-4 w-4 text-primary shrink-0" />}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {getPlatformIcon(creator.platform)}
                          <span>@{creator.username}</span>
                          <span>•</span>
                          <span>{formatFollowerCount(creator.follower_count)} followers</span>
                        </div>
                      </div>
                      {isStaged ? (
                        <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Added</Badge>
                      ) : (
                        <Button size="sm" onClick={() => addStagedAccount(creator)}>
                          <Plus className="h-3 w-3 mr-1" /> Add
                        </Button>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Staged Accounts Summary */}
            {stagedAccounts.length > 0 && (
              <Card className="border-primary">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Accounts to Link ({stagedAccounts.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {stagedAccounts.map((account, index) => (
                    <div key={`staged-${account.platform}-${account.username}`} className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <SafeAvatarImage src={getProxiedImageUrl(account.avatar_url)} className="object-cover" />
                        <AvatarFallback>{(account.display_name || account.username)?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium">{account.display_name || account.username}</span>
                        <span className="text-xs text-muted-foreground ml-2">@{account.username} • {account.platform}</span>
                      </div>
                      <Button variant="ghost" size="sm" className="text-destructive h-8" onClick={() => removeStagedAccount(index)}>
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    className="w-full mt-3"
                    onClick={() => batchClaimMutation.mutate(stagedAccounts)}
                    disabled={batchClaimMutation.isPending}
                  >
                    {batchClaimMutation.isPending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>
                    ) : (
                      <><Shield className="h-4 w-4 mr-2" />Submit All Accounts for Review</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* My Requests Tab */}
          <TabsContent value="requests" className="space-y-4">
            {requestsLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {!requestsLoading && (!myRequests || myRequests.length === 0) && (
              <div className="text-center py-12 text-muted-foreground">
                <Shield className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No claim requests yet</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setActiveTab('search')}>
                  Search for your profile
                </Button>
              </div>
            )}

            {myRequests && myRequests.length > 0 && (
              <div className="space-y-4">
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <Label className="text-sm font-medium whitespace-nowrap">Primary Platform:</Label>
                    <Select value={primaryPlatform} onValueChange={handlePrimaryPlatformChange}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select primary platform" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tiktok">TikTok</SelectItem>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="youtube">YouTube</SelectItem>
                      </SelectContent>
                    </Select>
                    {updatePrimaryPlatformMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">This tells brands which platform is your main presence</p>
                </Card>
                {myRequests.map((req) => (
                  <Card key={req.id} className="flex items-center p-4 gap-3">
                    <Avatar className="h-10 w-10">
                      <SafeAvatarImage src={getProxiedImageUrl(req.avatar_url)} className="object-cover" />
                      <AvatarFallback>{(req.display_name || req.username)?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{req.display_name || req.username}</div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {getPlatformIcon(req.platform)}
                        <span>@{req.username}</span>
                        {req.claimed_at && (
                          <>
                            <span>•</span>
                            <span>{new Date(req.claimed_at).toLocaleDateString()}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(req.claim_status || '')}
                      {req.claim_status === 'pending' && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-destructive">Cancel</Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Cancel claim?</AlertDialogTitle>
                              <AlertDialogDescription>This will remove your pending claim request.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep</AlertDialogCancel>
                              <AlertDialogAction onClick={() => cancelMutation.mutate(req.id)}>Cancel Claim</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
